import { createContext, useContext, ReactNode } from "react";
import { authClient, signIn, signOut, signUp, useSession } from "@/lib/auth-client";
import { api, unwrap } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";

// Public surface kept identical to the old Lovable hook so consumer
// components don't need to change. Internally backed by Better Auth
// (cookie session) + a /api/me query for grand_admin status.

type SessionUser = {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  emailVerified?: boolean;
};

interface AuthContextType {
  user: SessionUser | null;
  session: ReturnType<typeof useSession>["data"] | null;
  loading: boolean;
  isGrandAdmin: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (
    email: string,
    password: string,
    fullName: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const session = useSession();

  // /api/me tells us whether the current user is a grand admin and which
  // organizations they belong to. Cached for 5 min so navigation is snappy.
  const meQuery = useQuery({
    queryKey: ["me"],
    enabled: !!session.data?.user,
    queryFn: async () => {
      const r = await api.api.me.$get();
      return unwrap<{ user: SessionUser; isGrandAdmin: boolean }>(r);
    },
    staleTime: 5 * 60_000,
  });

  const user = (session.data?.user as SessionUser | undefined) ?? null;
  const loading = session.isPending || (!!user && meQuery.isPending);
  const isGrandAdmin = !!meQuery.data?.isGrandAdmin;

  const signInWithGoogle = async () => {
    // Better Auth's signIn.social returns a result object — it doesn't
    // throw on OAuth provider errors. We have to inspect .error so the
    // toast carries the actual reason ('redirect_uri_mismatch',
    // 'no_provider_config', etc.) instead of a generic 'failed'.
    const result = await signIn.social({
      provider: "google",
      callbackURL: `${window.location.origin}/apps`,
    });
    if (result?.error) {
      throw new Error(result.error.message ?? "Google sign-in failed");
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    const result = await signIn.email({ email, password });
    if (result.error) throw new Error(result.error.message);
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    fullName: string,
  ) => {
    const result = await signUp.email({
      email,
      password,
      name: fullName,
    });
    if (result.error) throw new Error(result.error.message);
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session: session.data ?? null,
        loading,
        isGrandAdmin,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        signOut: handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Convenience for components that previously used Better Auth-typed users
// (only the small subset we expose).
export type { SessionUser };

// Re-exported so callers can request OTP without importing auth-client.
export const requestEmailOtp = authClient.emailOtp.sendVerificationOtp;
export const verifyEmailOtp = authClient.signIn.emailOtp;
